import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";

import SearchSection from "./SearchSection";
import AdvancedFilters from "./AdvancedFilters";
import TournamentGrid from "./TournamentGrid";

import TournamentCardSkeleton from "./TournamentCardSkeleton";

import { getEventType } from "../../utility/eventType/eventType";
import { todayISODate } from "../../utils/homepageSeason";
import { locationRowsFromSquads } from "../../utility/organizationBowlingCenters";
import { listTournamentStatus } from "../../utility/tournamentProgress";
import { signupOpenByTournamentId } from "../../utility/signupEnablement";
import { fetchFindListTournaments } from "../../utility/findListTournaments";
import { seasonAnchor } from "../../utility/squadSpan";

const withSquadHouses = (list) =>
  (list || []).map((tournament) => ({
    ...tournament,
    tournamentLocations: locationRowsFromSquads(tournament.squads),
  }));

const listYear = (tournament) => {
  const anchor = seasonAnchor(tournament);
  return anchor ? anchor.slice(0, 4) : "";
};

const uniqueById = (items, nameKey) => {
  const byId = new Map();
  for (const item of items) {
    if (item?.id && item[nameKey]) byId.set(item.id, item);
  }
  return [...byId.values()].sort((a, b) =>
    String(a[nameKey]).localeCompare(String(b[nameKey]))
  );
};

const matchesEventType = (tournament, selectedEvent) => {
  const events = tournament.tournamentEvents || [];
  const pool = events.some((event) => event.isPrimary)
    ? events.filter((event) => event.isPrimary)
    : events;
  return pool.some(
    (event) => getEventType(event.bowlerQuantity) === selectedEvent
  );
};

const TournamentList = ({
  tournaments: propTournaments,
  filteredTournaments: propFilteredTournaments,
  setFilteredTournaments: propSetFilteredTournaments,
  showOrganizationFilter = true,
  onTournamentUpdate,
}) => {
  const [tournaments, setTournaments] = useState(propTournaments || []);
  const [filteredTournaments, setFilteredTournaments] = useState(
    propFilteredTournaments || []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [centerSearchTerm, setCenterSearchTerm] = useState("");
  const [showOrgResults, setShowOrgResults] = useState(false);
  const [showCenterResults, setShowCenterResults] = useState(false);
  const [loading, setLoading] = useState(!propTournaments);
  const [resultsVerified, setResultsVerified] = useState({});
  const [signupOpenById, setSignupOpenById] = useState({});

  // Use prop data if provided, otherwise fetch
  const isUsingProps = propTournaments !== undefined;

  // Get status options for filter
  const statusOptions = [
    { value: "", label: "All statuses" },
    { value: "Tournament Setup", label: "Tournament Setup" },
    { value: "Upcoming", label: "Upcoming" },
    { value: "Registration Open", label: "Registration Open" },
    { value: "Tournament Active", label: "Tournament Active" },
    { value: "Processing Standings", label: "Processing Standings" },
    { value: "Standings Available", label: "Standings Available" },
  ];

  // Get event options for filter
  const eventOptions = [
    { value: "", label: "All Events" },
    { value: "Singles", label: "Singles" },
    { value: "Doubles", label: "Doubles" },
    { value: "Team", label: "Team" },
  ];

  // Update local state when props change
  useEffect(() => {
    if (propTournaments !== undefined) {
      setTournaments(withSquadHouses(propTournaments));
    }
  }, [propTournaments]);

  useEffect(() => {
    if (propFilteredTournaments !== undefined) {
      setFilteredTournaments(withSquadHouses(propFilteredTournaments));
    }
  }, [propFilteredTournaments]);

  // Use prop setter if provided, otherwise use local setter
  const updateFilteredTournaments = (newFiltered) => {
    if (propSetFilteredTournaments) {
      propSetFilteredTournaments(newFiltered);
    } else {
      setFilteredTournaments(newFiltered);
    }
  };

  useEffect(() => {
    if (!isUsingProps) {
      const fetchTournaments = async () => {
        setLoading(true);
        const { data, error } = await fetchFindListTournaments(supabase);

        if (error) {
          console.error("Error fetching tournaments:", error);
          setLoading(false);
          return;
        }

        const sortedTournaments = withSquadHouses(data || []);
        setTournaments(sortedTournaments);
        setFilteredTournaments(sortedTournaments);
        setLoading(false);
      };

      fetchTournaments();
    }
  }, [isUsingProps]);

  useEffect(() => {
    const ids = tournaments.map((tournament) => tournament.id);
    if (ids.length === 0) {
      setResultsVerified({});
      setSignupOpenById({});
      return;
    }

    const loadListFlags = async () => {
      const [{ data: verificationData }, { data: signupData }] =
        await Promise.all([
          supabase
            .from("verificationStates")
            .select("tournamentId, isVerified")
            .eq("entityType", "results")
            .in("tournamentId", ids),
          supabase
            .from("tournamentSignupSettings")
            .select("tournamentID, allowSignups")
            .in("tournamentID", ids),
        ]);

      const verified = {};
      for (const row of verificationData || []) {
        if (row.isVerified) verified[row.tournamentId] = true;
      }
      setResultsVerified(verified);
      setSignupOpenById(signupOpenByTournamentId(ids, signupData));
    };

    loadListFlags();
  }, [tournaments]);

  useEffect(() => {
    filterTournaments();
  }, [
    searchTerm,
    selectedYear,
    selectedOrganization,
    selectedCenter,
    selectedStatus,
    selectedEvent,
    dateRange,
    tournaments,
    resultsVerified,
    signupOpenById,
    orgSearchTerm,
    centerSearchTerm,
  ]);

  const today = todayISODate();
  const tournamentsWithStatus = tournaments.map((tournament) => ({
    ...tournament,
    listStatus: listTournamentStatus(tournament, {
      resultsVerified: Boolean(resultsVerified[tournament.id]),
      allowSignups: Boolean(signupOpenById[tournament.id]),
      today,
    }),
  }));

  const filterTournaments = () => {
    let filtered = tournamentsWithStatus;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((tournament) =>
        tournament.name.toLowerCase().includes(term)
      );
    }

    if (selectedYear) {
      filtered = filtered.filter(
        (tournament) => listYear(tournament) === selectedYear
      );
    }

    if (showOrganizationFilter && selectedOrganization) {
      filtered = filtered.filter(
        (tournament) =>
          tournament.organizationID === selectedOrganization.id ||
          tournament.organizations?.id === selectedOrganization.id
      );
    } else if (showOrganizationFilter && orgSearchTerm.trim()) {
      const term = orgSearchTerm.trim().toLowerCase();
      filtered = filtered.filter((tournament) =>
        tournament.organizations?.name?.toLowerCase().includes(term)
      );
    }

    if (dateRange.start) {
      filtered = filtered.filter((tournament) => {
        const anchor = seasonAnchor(tournament);
        return anchor && anchor >= dateRange.start;
      });
    }
    if (dateRange.end) {
      filtered = filtered.filter((tournament) => {
        const anchor = seasonAnchor(tournament);
        return anchor && anchor <= dateRange.end;
      });
    }

    if (selectedCenter) {
      filtered = filtered.filter((tournament) =>
        tournament.tournamentLocations?.some(
          (location) => location.bowlingCenters?.id === selectedCenter.id
        )
      );
    } else if (centerSearchTerm.trim()) {
      const term = centerSearchTerm.trim().toLowerCase();
      filtered = filtered.filter((tournament) =>
        tournament.tournamentLocations?.some((location) =>
          location.bowlingCenters?.centerName?.toLowerCase().includes(term)
        )
      );
    }

    if (selectedEvent) {
      filtered = filtered.filter((tournament) =>
        matchesEventType(tournament, selectedEvent)
      );
    }

    if (selectedStatus) {
      filtered = filtered.filter(
        (tournament) => tournament.listStatus === selectedStatus
      );
    }

    updateFilteredTournaments(filtered);
  };

  const handleDeleteTournament = async (tournamentId, tournamentName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${tournamentName}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const { error } = await supabase
        .from("tournaments")
        .update({ isDeleted: true })
        .eq("id", tournamentId);

      if (error) throw error;

      // Update tournaments list
      const updatedTournaments = tournaments.filter(
        (tournament) => tournament.id !== tournamentId
      );
      setTournaments(updatedTournaments);

      // Call callback if provided
      if (onTournamentUpdate) {
        onTournamentUpdate();
      }
    } catch (error) {
      console.error("Error deleting tournament:", error);
    }
  };

  const years = [
    ...new Set(
      tournaments
        .map((tournament) => listYear(tournament))
        .filter(Boolean)
    ),
  ].sort((a, b) => b.localeCompare(a));

  const organizations = uniqueById(
    tournaments.map((tournament) => tournament.organizations).filter(Boolean),
    "name"
  );
  const bowlingCenters = uniqueById(
    tournaments.flatMap((tournament) =>
      (tournament.tournamentLocations || [])
        .map((location) => location.bowlingCenters)
        .filter(Boolean)
    ),
    "centerName"
  );

  const filteredCenters = bowlingCenters.filter((center) =>
    center?.centerName
      ?.toLowerCase()
      .includes(centerSearchTerm?.toLowerCase() || "")
  );

  const filteredOrgs = organizations.filter((org) =>
    org?.name?.toLowerCase().includes(orgSearchTerm?.toLowerCase() || "")
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <SearchSection
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          showAdvancedSearch={showAdvancedSearch}
          setShowAdvancedSearch={setShowAdvancedSearch}
        />

        {showAdvancedSearch && (
          <AdvancedFilters
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            selectedOrganization={selectedOrganization}
            setSelectedOrganization={setSelectedOrganization}
            selectedCenter={selectedCenter}
            setSelectedCenter={setSelectedCenter}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedEvent={selectedEvent}
            setSelectedEvent={setSelectedEvent}
            dateRange={dateRange}
            setDateRange={setDateRange}
            years={years}
            statusOptions={statusOptions}
            eventOptions={eventOptions}
            organizations={filteredOrgs}
            bowlingCenters={filteredCenters}
            orgSearchTerm={orgSearchTerm}
            setOrgSearchTerm={setOrgSearchTerm}
            centerSearchTerm={centerSearchTerm}
            setCenterSearchTerm={setCenterSearchTerm}
            showOrgResults={showOrgResults}
            setShowOrgResults={setShowOrgResults}
            showCenterResults={showCenterResults}
            setShowCenterResults={setShowCenterResults}
            showOrganizationFilter={showOrganizationFilter}
          />
        )}

        {/* Loading Skeleton */}
        <div className="mb-4">
          <div className="h-4 w-40 bg-brand-charcoal/10"></div>
        </div>

        <div className="border-t border-brand-charcoal/15">
          {Array.from({ length: 6 }).map((_, index) => (
            <TournamentCardSkeleton key={index} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SearchSection
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        showAdvancedSearch={showAdvancedSearch}
        setShowAdvancedSearch={setShowAdvancedSearch}
      />

      {showAdvancedSearch && (
        <AdvancedFilters
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          selectedOrganization={selectedOrganization}
          setSelectedOrganization={setSelectedOrganization}
          selectedCenter={selectedCenter}
          setSelectedCenter={setSelectedCenter}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          selectedEvent={selectedEvent}
          setSelectedEvent={setSelectedEvent}
          dateRange={dateRange}
          setDateRange={setDateRange}
          years={years}
          statusOptions={statusOptions}
          eventOptions={eventOptions}
          organizations={filteredOrgs}
          bowlingCenters={filteredCenters}
          orgSearchTerm={orgSearchTerm}
          setOrgSearchTerm={setOrgSearchTerm}
          centerSearchTerm={centerSearchTerm}
          setCenterSearchTerm={setCenterSearchTerm}
          showOrgResults={showOrgResults}
          setShowOrgResults={setShowOrgResults}
          showCenterResults={showCenterResults}
          setShowCenterResults={setShowCenterResults}
          showOrganizationFilter={showOrganizationFilter}
        />
      )}

      <TournamentGrid
        tournaments={filteredTournaments}
        onDelete={handleDeleteTournament}
      />
    </div>
  );
};

export default TournamentList;
